import { useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { DELETE_CLIENT_WITH_RELATED_DATA_MUTATION } from 'src/graphql/entities/client/client.operations';
import { useGroupRules, UserGroupRules } from 'src/graphql/entities/group-rules';
import { generateFieldModifiersMap } from 'src/graphql/utils';
import { useNotifications } from 'src/utils/hooks';

type DeleteClientWithRelatedDataResponse = {
  deleteClientWithRelatedData: {
    clientId: string;
  };
};

export const useDeleteClient = () => {
  const { showSuccessAlert, showErrorAlert } = useNotifications();
  const { groupRules } = useGroupRules();

  const [deleteClientMutation, { loading }] =
    useMutation<DeleteClientWithRelatedDataResponse>(
      DELETE_CLIENT_WITH_RELATED_DATA_MUTATION,
      {
        update: (cache, result) => {
          const clientId = result.data?.deleteClientWithRelatedData?.clientId;
          if (!clientId) {
            return;
          }
          // Soft delete: mark the client deleted in the cache rather than evicting it. Evicting
          // would orphan embedded references — e.g. a conversation participant's `client`, which
          // the chat reads to render a message sender's name — collapsing them to null with no way
          // for restore to bring them back (restore only `cache.modify`s the Client entity, which
          // is a no-op once evicted). Keeping the entity lets `deletedAt` drive the "(Deleted)"
          // treatment reactively and lets restore clear it. Active-only lists are filtered
          // server-side (`deleted_at IS NULL`).
          cache.modify({
            id: cache.identify({ __typename: 'Client', id: clientId }),
            fields: generateFieldModifiersMap({
              deletedAt: new Date().toISOString(),
              __typename: 'Client',
            }),
          });
        },
        onCompleted: () => {
          showSuccessAlert('Client was deleted');
        },
        onError: () => {
          showErrorAlert('Could not delete client, please try again');
        },
      },
    );

  const deleteClient = useCallback(
    async (clientId: string) => {
      if (!groupRules[UserGroupRules.AccessToDeleteClients]) {
        return;
      }
      // The portal only performs soft deletes; permanent deletion is backend/admin-only.
      await deleteClientMutation({
        variables: {
          params: {
            clientId,
            softDelete: true,
          },
        },
      });
    },
    [groupRules, deleteClientMutation],
  );

  return { deleteClient, isLoading: loading };
};
